import React, { useCallback, useMemo } from 'react';
import { RouteComponentProps, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { TranslationWithParams } from 'src/app/i18n';
import {
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import Form from 'src/app/common/components/Form';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import commonStyles from 'src/app/common/styles/page.module.css';
import { useStyles } from './registration-detail.style';
import { useRegistrationDetail } from './registration-detail.hook';
import { ModulePermissionProps } from 'src/app/common/module/types';
import {
  PaymentTypeEnum,
  RegistrationApprovalEventsEnum,
  ExamEventsEnum,
  ResultTypeEnum,
  RegistrationApprovalStatesEnum,
} from '../../../enum/license-exam.enum';
import { eventType, PaymentType, resultType } from '../../../types/license-exam-types';
import { LoadingButton } from '@mui/lab';
import { scoreValidator } from '../util/form.util';
import { dateFormatToLocal, isValidHkId, isValidPassport, utcToLocalDate } from '../../../utils';
import { getConfigurations } from 'src/app/common/utils';
import { get } from 'lodash';

type PageProps = ParamsProps & ModulePermissionProps & RouteComponentProps;

interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'select';
  required?: boolean;
  options?: { value: string; label: string }[];
  format?: string;
  xs?: number;
}

export const RegistrationDetailPage: React.FC<PageProps> = (props: PageProps) => {
  const { history, enableUpdate } = props;
  const { id } = useParams<{ id: string }>();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const config = getConfigurations();
  const enablePaymentInfo = get(config, 'Recruitment.enableLicenseExamPayment', false);

  // style
  const { classes } = useStyles();
  const styles = useMemo(() => classes, [classes]);

  // custom-hook
  const {
    action,
    data,
    handlePress,
    dialogState,
    handleCloseDialog,
    showResult,
    showCancelButton,
    showApprovalButtons,
    editing,
    setEditing,
    handleSaveEditResult,
    handleConfirm,
    remark,
    setRemark,
    form,
    initialValues,
    loadingUpdateResult,
    loadingApprove,
    statusLabel,
    editingExamineeDetails,
    setEditingExamineeDetails,
    handleSaveExamineeDetails,
    editExamineeform,
    examineeDetails,
    loadingEditExamineeDetails,
  } = useRegistrationDetail({
    id: id,
    enableUpdate: enableUpdate,
  });

  const fieldConfigs: FieldConfig[] = [
    {
      name: 'firstname',
      label: 'recruitment.exam.registration.firstName',
      type: 'text',
      required: true,
      xs: 3,
    },
    {
      name: 'lastname',
      label: 'recruitment.exam.registration.lastName',
      type: 'text',
      required: true,
      xs: 3,
    },
    {
      name: 'localName',
      label: 'localName',
      type: 'text',
      required: false,
      xs: 3,
    },
    {
      name: 'hkId',
      label: 'recruitment.exam.hkid.indicator',
      type: 'select',
      required: true,
      options: [
        { value: 'Y', label: 'Y' },
        { value: 'N', label: 'N' },
      ],
      xs: 3,
    },
    {
      name: 'identityDocumentNumber',
      label: 'identityDocumentNumber',
      type: 'text',
      required: true,
      xs: 3,
    },
    {
      name: 'birthday',
      label: 'birthday',
      type: 'text',
      required: true,
      format: '(DD/MM/YYYY)',
      xs: 3,
    },
  ];

  const renderField = useCallback(
    (config: FieldConfig) => {
      const { name, label, type, required, format, xs = 3 } = config;
      const selectOptions = config.options || [];
      let rules: any[] = [];

      if (required) {
        rules.push({ required: true, message: `${Translation(label)} ${Translation('component.form-required-text')}` });
      }

      // Verify birthday format
      if (format && format.includes('DD/MM/YYYY')) {
        rules.push({
          pattern: /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
          message: `${Translation('agent_referral_link_tnc_rule')}`,
        });
      }

      // Local name validation: if initialValue has value, localName is required
      if (name === 'localName') {
        rules.push({
          validator: (_: any, value: string) => {
            if (examineeDetails?.localName && !value) {
              return Promise.reject(new Error(`${Translation(label)} ${Translation('component.form-required-text')}`));
            }
            return Promise.resolve();
          },
        });
      }

      // verify HKID/Passport format
      if (name === 'identityDocumentNumber') {
        rules.push({
          validator: (_: any, value: string) => {
            const hkIdValue = editExamineeform.getFieldValue('hkId') || examineeDetails?.hkId;
            if (!value) {
              return Promise.resolve(); //  handle by required
            }
            if (hkIdValue === 'Y') {
              if (!isValidHkId(value)) {
                return Promise.reject(new Error(`${Translation('Common.input_correct_hkid')}`));
              }
            } else {
              if (!isValidPassport(value)) {
                return Promise.reject(new Error(`${Translation('Common.invalid_input')}`));
              }
            }
            return Promise.resolve();
          },
        });
      }
      if (type === 'select') {
        return (
          <Grid item xs={xs} key={name}>
            <Form.Item name={name} rules={rules}>
              {({ value, onChange }) => (
                <FormControl variant="outlined" fullWidth disabled={!editingExamineeDetails}>
                  <InputLabel>{Translation(label)} </InputLabel>
                  <Select
                    value={value}
                    label={Translation(label)}
                    onChange={(e: SelectChangeEvent<string>) => {
                      onChange(e.target.value);
                    }}
                  >
                    {selectOptions.map((option): React.ReactElement => {
                      return (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Form.Item>
          </Grid>
        );
      }
      return (
        <Grid item xs={xs} key={name}>
          <Form.Item name={name} rules={rules}>
            <TextField
              variant="outlined"
              fullWidth
              label={format ? `${Translation(label)} ${format}` : Translation(label)}
              InputProps={{
                readOnly: !editingExamineeDetails,
              }}
            />
          </Form.Item>
        </Grid>
      );
    },
    [editingExamineeDetails],
  );
  return (
    <>
      <Dialog open={dialogState.open} onClose={handleCloseDialog}>
        <div className={styles.dialogContainer}>
          <div className={styles.dialogTitle}>{dialogState.header}</div>
          {action === RegistrationApprovalEventsEnum.cancel && (
            <div className={styles.dialogSubtitle}>{Translation('recruitment.exam.registration.cancel.subtitle')}</div>
          )}
          <div className={styles.content}>
            <TextField
              variant="outlined"
              label={Translation('recruitment.exam.registration.remark')}
              value={remark}
              fullWidth
              onChange={(e) => {
                setRemark(e.target.value);
              }}
            />
          </div>
          <div className={styles.dialogButton}>
            <Button variant="outlined" color="inherit" onClick={handleCloseDialog}>
              {Translation('app.button.cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              color="secondary"
              loading={loadingApprove}
              className={styles.buttonMargin}
              onClick={handleConfirm}
            >
              {Translation('app.button.confirm')}
            </LoadingButton>
          </div>
        </div>
      </Dialog>
      <div className={commonStyles.headerContainer} style={{ justifyContent: 'space-between' }}>
        <div className={commonStyles.rowContainer}>
          <div className={commonStyles.header}>{Translation('recruitment.exam.registration.detail')}</div>
        </div>
        <div>
          {enableUpdate && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setEditingExamineeDetails(true);
              }}
            >
              {Translation('app.button.edit')}
            </Button>
          )}
          <Button variant="outlined" color="inherit" className={styles.buttonMargin} onClick={history.goBack}>
            {Translation('app.button.back')}
          </Button>
        </div>
      </div>
      {data && (
        <>
          <div className={styles.container}>
            <Form form={editExamineeform} onFinish={handleSaveExamineeDetails} initialValues={{ ...examineeDetails }}>
              <Grid container spacing={3} className={styles.wrap}>
                <Grid item xs={4}>
                  <div className={styles.header}>{Translation('recruitment.exam.registration.id')}</div>
                  <div className={styles.header}>{data.registrationId || '-'}</div>
                </Grid>
                <Grid item xs={3}>
                  <div className={styles.header}>{Translation('recruitment.exam.registration.application.status')}</div>
                  <div className={styles.header}>{statusLabel}</div>
                </Grid>
              </Grid>
              <div className={styles.header}>{Translation('recruitment.exam.registration.examinee')}</div>
              <Grid container spacing={3} className={styles.wrap}>
                <Grid item xs={3}>
                  <TextField
                    variant="outlined"
                    label={Translation('recruitment.exam.registration.applicantId')}
                    value={data.applicantId || '-'}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                {fieldConfigs.map((config) => renderField(config))}
              </Grid>
              <div className={styles.header}>{Translation('recruitment.exam.registration.schedule')}</div>
              <Grid container spacing={3} className={styles.wrap}>
                <Grid item xs={3}>
                  <TextField
                    variant="outlined"
                    label={Translation('recruitment.exam.examCode')}
                    value={data.examCode || '-'}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    variant="outlined"
                    label={Translation('recruitment.exam.examType')}
                    value={data.examType || '-'}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    variant="outlined"
                    label={Translation('recruitment.exam.examDate')}
                    value={data.examDate ? dateFormatToLocal(data.examDate) : '-'}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    variant="outlined"
                    label={Translation('recruitment.exam.examDateStartTime')}
                    value={data.startTime || '-'}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    variant="outlined"
                    label={Translation('recruitment.exam.examDateEndTime')}
                    value={data.endTime || '-'}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    variant="outlined"
                    label={Translation('recruitment.exam.examCentre')}
                    value={data.examCentre || '-'}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    variant="outlined"
                    label={Translation('recruitment.exam.registration.address')}
                    value={data.address || '-'}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
              </Grid>
              {enablePaymentInfo && (
                <>
                  <div className={styles.header}>{Translation('recruitment.exam.registration.proofOfPayment')}</div>
                  <Grid container spacing={3} className={styles.wrap}>
                    <Grid item xs={3}>
                      <TextField
                        variant="outlined"
                        label={Translation('recruitment.exam.fee.number')}
                        value={data.examFeeReceiptNo || '-'}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        FormHelperTextProps={{ sx: { color: '#E8192C' } }}
                        helperText={
                          data.examFeeOrNumberUsedBefore &&
                          data.registrationApprovalStates !== RegistrationApprovalStatesEnum.REGISTERED.toUpperCase()
                            ? Translation('recruitment.common.or.error.alert')
                            : null
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        variant="outlined"
                        label={Translation('recruitment.exam.fee.date')}
                        value={data.examFeeReceiptDate ? dateFormatToLocal(data.examFeeReceiptDate) : '-'}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} className={styles.wrap}>
                    <Grid item xs={3}>
                      <TextField
                        variant="outlined"
                        label={Translation('recruitment.license.fee.type')}
                        value={
                          data.paymentType
                            ? Translation(
                                `recruitment.exam.registration.payment.${
                                  PaymentTypeEnum[data.paymentType as PaymentType]
                                }`,
                              )
                            : '-'
                        }
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        variant="outlined"
                        label={Translation('recruitment.license.fee.number')}
                        value={
                          data.licenseFeeReceiptNo &&
                          data.paymentType &&
                          data.paymentType !== PaymentTypeEnum.CODE_NOW_PAY_LATER.toUpperCase()
                            ? data.licenseFeeReceiptNo
                            : '-'
                        }
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        FormHelperTextProps={{ sx: { color: '#E8192C' } }}
                        helperText={
                          data.licenseFeeOrNumberUsedBefore &&
                          data.registrationApprovalStates !== RegistrationApprovalStatesEnum.REGISTERED.toUpperCase()
                            ? Translation('recruitment.common.or.error.alert')
                            : null
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        variant="outlined"
                        label={Translation('recruitment.license.fee.date')}
                        value={
                          data.licenseFeeReceiptDate &&
                          data.paymentType &&
                          data.paymentType !== PaymentTypeEnum.CODE_NOW_PAY_LATER.toUpperCase()
                            ? dateFormatToLocal(data.licenseFeeReceiptDate)
                            : '-'
                        }
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
              {editingExamineeDetails && (
                <div style={{ textAlign: 'end' }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => {
                      editExamineeform.resetFields();
                      setEditingExamineeDetails(false);
                    }}
                  >
                    {Translation('app.button.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" color="secondary" className={styles.buttonMargin}>
                    {Translation('app.button.save')}
                    {loadingEditExamineeDetails && <CircularProgress style={{ marginLeft: 8 }} size={15} />}
                  </Button>
                </div>
              )}
            </Form>
          </div>

          {showResult && (
            <Form form={form} onFinish={handleSaveEditResult} initialValues={{ ...initialValues }}>
              <div className={commonStyles.headerContainer}>
                <div className={commonStyles.header}>{Translation('recruitment.exam.registration.examResult')}</div>
                {editing ? (
                  <div>
                    <Button variant="outlined" color="inherit" onClick={() => setEditing(false)}>
                      {Translation('app.button.cancel')}
                    </Button>
                    <Button type="submit" variant="contained" color="secondary" className={styles.buttonMargin}>
                      {Translation('app.button.save')}
                      {loadingUpdateResult && <CircularProgress style={{ marginLeft: 8 }} size={15} />}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={!enableUpdate}
                    onClick={() => setEditing(true)}
                  >
                    {Translation('app.button.edit')}
                  </Button>
                )}
              </div>
              <div className={styles.container}>
                {editing ? (
                  <>
                    <Form.Item
                      name="result"
                      label={Translation('recruitment.exam.registration.result')}
                      rules={[{ required: true, message: Translation('component.form-required-text') }]}
                      shouldUpdate
                    >
                      <FormControl error>
                        <RadioGroup
                          className={classes.radioGroup}
                          aria-label="exam-result"
                          name="exam-result"
                          defaultValue={initialValues.result}
                        >
                          {Object.entries(ResultTypeEnum).map(([value, label]) => (
                            <FormControlLabel
                              style={{ marginBottom: 0 }}
                              key={value}
                              value={value}
                              control={<Radio />}
                              label={Translation(`recruitment.exam.registration.result.${label}`)}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Form.Item>
                    <Form.Item
                      name="score"
                      required={false} //PHKL Custom -s
                      label={Translation('recruitment.exam.registration.score')}
                      rules={[
                        {
                          required: false, //PHKL Custom -s
                          validator(rule, value, callback) {
                            const msg = scoreValidator(
                              {
                                rule,
                                emptyMsg: Translation('component.form-required-text'),
                                errorMsg: Translation('recruitment.exam.score_value_validator'),
                              },
                              value,
                              form.getFieldValue('result'),
                            );
                            callback(msg);
                          },
                        },
                      ]}
                    >
                      <TextField
                        margin="dense"
                        variant="outlined"
                        type="number"
                        InputProps={{ inputProps: { min: 0, step: '0.00001' } }}
                      />
                    </Form.Item>
                  </>
                ) : (
                  <>
                    <div className={styles.rowContainer}>
                      <div className={styles.formHeader}>
                        {Translation('recruitment.exam.registration.result')}
                        <span className={classes.mandatory}>*</span>
                      </div>
                      <span>
                        {data.result
                          ? Translation(
                              `recruitment.exam.registration.result.${ResultTypeEnum[data.result as resultType]}`,
                            )
                          : '-'}
                      </span>
                    </div>

                    <div className={styles.rowContainer}>
                      <div className={styles.formHeader}>
                        {Translation('recruitment.exam.registration.score')}
                        {/* PHKL Custom -s */}
                        {/* <span className={classes.mandatory}>*</span> */}
                        {/* PHKL Custom -e */}
                      </div>
                      <span>{data.score || '-'}</span>
                    </div>
                  </>
                )}
              </div>
            </Form>
          )}
          {data?.submissionTime && (
            <>
              <div className={commonStyles.header}>{Translation('recruitment.exam.registration.record')}</div>
              <div className={styles.container}>
                <ul className={styles.list}>
                  {data.operationHistoryList?.map((history, index) => (
                    <li key={index}>
                      <span>
                        {history.event
                          ? history.event === RegistrationApprovalEventsEnum.submit
                            ? TranslationWithParams(`recruitment.exam.registration.remark.submit`, {
                                operationUser: history.operator.operatorName, //PHKL Custom
                                time: utcToLocalDate(history.updatedAt),
                              })
                            : TranslationWithParams(
                                `recruitment.exam.registration.remark.${ExamEventsEnum[history.event as eventType]}`,
                                {
                                  operationUser: history.event !== 'EXPIRE' ? history.operator.operatorName : undefined,
                                  time: utcToLocalDate(history.updatedAt),
                                },
                              )
                          : '-'}
                      </span>
                      {history.remark && (
                        <span>{` ${Translation('recruitment.exam.registration.remark')}: ${history.remark}`}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          <div className={styles.buttonGroup}>
            {showCancelButton && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => handlePress(RegistrationApprovalEventsEnum.cancel)}
              >
                {Translation('recruitment.exam.registration.cancel.button')}
              </Button>
            )}
            {showApprovalButtons && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  className={styles.buttonMargin}
                  onClick={() => handlePress(RegistrationApprovalEventsEnum.reject)}
                >
                  {Translation('recruitment.exam.registration.reject')}
                </Button>
                {/* PCAAEB-53815: Hide "Return" button on registration details
                <Button
                  variant="contained"
                  color="secondary"
                  className={styles.buttonMargin}
                  onClick={() => handlePress(RegistrationApprovalEventsEnum.return)}
                >
                  {Translation('recruitment.exam.registration.return')}
                </Button> */}
                <Button
                  variant="contained"
                  color="secondary"
                  className={styles.buttonMargin}
                  onClick={() => handlePress(RegistrationApprovalEventsEnum.approve)}
                >
                  {Translation('recruitment.exam.registration.approve')}
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
